import useSWR from 'swr'
import { z } from 'zod'
import { DataSnapshotSchema, type DataSnapshot } from '@/lib/schemas/snapshot'
import { fetchJson } from '@/lib/fetchJson'
import { usePersistedSnapshot } from '@/hooks/usePersistedSnapshot'
import { useStaleState } from '@/hooks/useStaleState'
import type { FreshnessState } from '@/types/status'

export interface UseSnapshotResult<TData> {
  data: TData | undefined
  updatedAt: string | undefined
  isLoading: boolean
  isError: boolean
  error: Error | undefined
  freshness: FreshnessState
  ageMs: number
  isPersistedFallback: boolean
  mutate: () => void
}

/**
 * Primary data hook for module snapshots.
 * Fetches, validates, persists, and computes freshness in one call.
 */
export function useSnapshot<TData>(
  url: string | null,
  moduleId: string,
  endpoint: string,
  dataSchema: z.ZodType<TData>,
  refreshInterval?: number
): UseSnapshotResult<TData> {
  const fullSchema = DataSnapshotSchema(dataSchema)
  type Snap = DataSnapshot<TData>

  const fetcher = (u: string): Promise<Snap> =>
    fetchJson(u, fullSchema) as Promise<Snap>

  const { data: live, isLoading, error, mutate } = useSWR<Snap, Error>(
    url,
    fetcher,
    { refreshInterval }
  )

  const { persisted } = usePersistedSnapshot<Snap>(moduleId, endpoint, live)

  const effectiveData = live ?? persisted ?? undefined
  const isPersistedFallback = live === undefined && persisted !== null
  const updatedAt = effectiveData?.updatedAt

  const { freshness, ageMs } = useStaleState(updatedAt, refreshInterval)

  return {
    data: effectiveData?.data,
    updatedAt,
    isLoading,
    isError: !!error,
    error: error as Error | undefined,
    freshness,
    ageMs,
    isPersistedFallback,
    mutate,
  }
}
